import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AINewSettingsTabs from "./AINewSettingsTabs";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { LoadingSpinner } from "@velaro/velaro-component-lib";

// ── Types ─────────────────────────────────────────────────────────────────────

interface KnowledgeOverride {
  id: number;
  topic: string;
  overrideFact: string;
  searchTerms: string;
  priority: number;
  startsAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

const EMPTY_FORM = {
  topic: "",
  overrideFact: "",
  searchTerms: "",
  priority: 0,
  startsAt: "",
  expiresAt: "",
  isActive: true
};

// ── Helpers ───────────────────────────────────────────────────────────────────

type OverrideStatus = "active" | "scheduled" | "expired" | "inactive";

function getOverrideStatus(o: KnowledgeOverride): OverrideStatus {
  if (!o.isActive) return "inactive";
  const now = new Date();
  if (o.startsAt && new Date(o.startsAt) > now) return "scheduled";
  if (o.expiresAt && new Date(o.expiresAt) < now) return "expired";
  return "active";
}

const STATUS_STYLES: Record<OverrideStatus, string> = {
  active:    "bg-green-100 text-green-700",
  scheduled: "bg-blue-100 text-blue-700",
  expired:   "bg-red-100 text-red-700",
  inactive:  "bg-slate-100 text-slate-500"
};

const STATUS_LABELS: Record<OverrideStatus, string> = {
  active:    "Active",
  scheduled: "Scheduled",
  expired:   "Expired",
  inactive:  "Off"
};

// Common quick-fill templates for timed overrides
interface OverrideTemplate {
  label: string;
  topic: string;
  fact: string;
  durationDays?: number;
}

const QUICK_TEMPLATES: OverrideTemplate[] = [
  {
    label: "Closed today",
    topic: "Today's Hours",
    fact: "We are closed today. We will reopen on our regular schedule tomorrow.",
    durationDays: 1
  },
  {
    label: "Holiday closure",
    topic: "Holiday Hours",
    fact: "We are closed for the holiday. We will reopen on [Date]. Thank you for your patience.",
    durationDays: 3
  },
  {
    label: "Extended holiday",
    topic: "Holiday Hours",
    fact: "Our offices are closed for the holiday season from [StartDate] through [EndDate]. We will resume normal operations on [ReopenDate].",
    durationDays: 7
  },
  {
    label: "Limited hours",
    topic: "Current Hours",
    fact: "We are currently operating with limited hours. Our temporary schedule is [Hours]. Thank you for your understanding.",
    durationDays: 14
  }
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function AINewSettingsKnowledgeOverrides() {
  const { id } = useParams();
  const api = useApi();
  const toastAlert = useToastAlert();

  const [overrides, setOverrides] = useState<KnowledgeOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState<KnowledgeOverride | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const res = await api.admin.get("KnowledgeOverride");
      const data = await res.json();
      setOverrides(Array.isArray(data) ? data : []);
    } catch {
      setOverrides([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(o: KnowledgeOverride) {
    setEditTarget(o);
    setForm({
      topic: o.topic,
      overrideFact: o.overrideFact,
      searchTerms: o.searchTerms ?? "",
      priority: o.priority,
      startsAt: o.startsAt ? o.startsAt.split("T")[0] : "",
      expiresAt: o.expiresAt ? o.expiresAt.split("T")[0] : "",
      isActive: o.isActive
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
  }

  async function handleSave() {
    if (!form.topic.trim()) {
      toastAlert.displayToast(Severity.Error, "Topic is required.");
      return;
    }
    if (!form.overrideFact.trim()) {
      toastAlert.displayToast(Severity.Error, "Fact text is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        topic: form.topic.trim(),
        overrideFact: form.overrideFact.trim(),
        searchTerms: form.searchTerms.trim() || null,
        priority: Number(form.priority) || 0,
        startsAt: form.startsAt ? form.startsAt : null,
        expiresAt: form.expiresAt ? form.expiresAt : null,
        isActive: form.isActive
      };

      let res;
      if (editTarget) {
        res = await api.admin.put(`KnowledgeOverride/${editTarget.id}`, payload);
      } else {
        res = await api.admin.post("KnowledgeOverride", payload);
      }

      const data = await res.json();
      if (data?.success) {
        toastAlert.displayToast(
          Severity.Success,
          editTarget ? "Override updated." : "Override created."
        );
        closeForm();
        await load();
      } else {
        toastAlert.displayToast(Severity.Error, data?.message ?? "Save failed — please try again.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Save failed — see browser console.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(o: KnowledgeOverride) {
    if (!window.confirm(`Delete override "${o.topic}"? This cannot be undone.`)) return;

    try {
      const res = await api.admin.delete(`KnowledgeOverride/${o.id}`);
      const data = await res.json();
      if (data?.success) {
        toastAlert.displayToast(Severity.Success, "Override deleted.");
        await load();
      } else {
        toastAlert.displayToast(Severity.Error, data?.message ?? "Delete failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Delete failed — see browser console.");
    }
  }

  async function handleToggle(o: KnowledgeOverride) {
    try {
      const res = await api.admin.patch(`KnowledgeOverride/${o.id}/toggle`, {});
      const data = await res.json();
      if (data?.success) {
        setOverrides((prev) =>
          prev.map((x) => (x.id === o.id ? { ...x, isActive: data.isActive } : x))
        );
      } else {
        toastAlert.displayToast(Severity.Error, "Toggle failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Toggle failed.");
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id ?? ""} />

      <div className="max-w-4xl mx-auto w-full px-4 pb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Knowledge Overrides</h2>
            <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
              Define authoritative facts that are always injected into the AI prompt — before vector
              search results. Use these to guarantee the AI uses your exact policy text for
              brand-critical topics like returns, warranties, pricing, or holiday hours.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap ml-4"
          >
            + Add Override
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800 space-y-1.5">
          <p>
            <strong>Search priority order (first hit wins):</strong>
          </p>
          <ol className="list-decimal list-inside space-y-0.5 text-blue-700 ml-1">
            <li><strong>Knowledge Overrides</strong> — always injected verbatim (this page)</li>
            <li><strong>Q&amp;A pairs</strong> — searched by semantic similarity (<Link to={`/ai-new/settings/qna/${id ?? ""}`} className="underline hover:text-blue-900">manage Q&amp;A →</Link>)</li>
            <li><strong>Knowledge Base</strong> — vector search of scraped/uploaded content</li>
          </ol>
          <p className="text-blue-700 mt-1">
            Overrides without trigger keywords are always injected. With keywords (e.g.{" "}
            <code className="bg-blue-100 px-1 rounded">warranty, return</code>) they are only
            injected when the customer&apos;s message contains one of those words.
            Use <strong>timed overrides</strong> for holiday closures, temporary hours, or upcoming events.
          </p>
        </div>

        {overrides.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">
            <div className="text-3xl mb-2">📋</div>
            <div className="font-medium mb-1">No overrides yet</div>
            <div className="text-sm">
              Add your first override to guarantee exact answers for your most important policies.
            </div>
          </div>
        )}

        {overrides.length > 0 && (
          <div className="flex flex-col gap-3">
            {overrides.map((o) => {
              const status = getOverrideStatus(o);
              return (
              <div
                key={o.id}
                className={`bg-white border rounded-xl px-5 py-4 shadow-sm flex flex-col gap-2 ${
                  status === "expired" ? "border-red-100 opacity-70" :
                  status === "inactive" ? "border-slate-100 opacity-60" :
                  "border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800">{o.topic}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[status]}`}>
                      {STATUS_LABELS[status]}
                    </span>
                    {o.priority > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        Priority {o.priority}
                      </span>
                    )}
                    {o.searchTerms && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        Keywords: {o.searchTerms}
                      </span>
                    )}
                    {o.startsAt && new Date(o.startsAt) > new Date() && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        Starts {new Date(o.startsAt).toLocaleDateString()}
                      </span>
                    )}
                    {o.expiresAt && new Date(o.expiresAt) >= new Date() && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        Expires {new Date(o.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                    {o.expiresAt && new Date(o.expiresAt) < new Date() && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        Expired {new Date(o.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                    {o.usageCount > 0 && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {o.usageCount.toLocaleString()} uses
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleToggle(o)}
                      aria-label={o.isActive ? "Deactivate override" : "Activate override"}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        o.isActive ? "bg-blue-600" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          o.isActive ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => openEdit(o)}
                      className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(o)}
                      className="text-sm text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      aria-label={`Delete override ${o.topic}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 whitespace-pre-wrap">
                  {o.overrideFact}
                </div>
              </div>
            );})}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={closeForm} aria-hidden="true" />
          <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-slate-800">
                {editTarget ? "Edit Override" : "New Override"}
              </h3>
              <button
                onClick={closeForm}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                aria-label="Close drawer"
              >
                ×
              </button>
            </div>
            <div className="flex flex-col gap-5 px-6 py-6 flex-1">
              {/* Quick templates (only on new override) */}
              {!editTarget && (
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-1.5">Quick fill from template:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_TEMPLATES.map((t) => {
                      const today = new Date().toISOString().split("T")[0];
                      const expiry = t.durationDays
                        ? new Date(Date.now() + t.durationDays * 86400000).toISOString().split("T")[0]
                        : "";
                      return (
                        <button
                          key={t.label}
                          type="button"
                          onClick={() => setForm((f) => ({
                            ...f,
                            topic: t.topic,
                            overrideFact: t.fact,
                            startsAt: today,
                            expiresAt: expiry,
                            searchTerms: "hours, open, closed, holiday"
                          }))}
                          className="px-2.5 py-1 text-xs border border-slate-300 rounded-lg hover:bg-blue-50 hover:border-blue-300 text-slate-600 transition-colors"
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Topic <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.topic}
                  onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                  placeholder="e.g. Return Policy, Holiday Hours, Warranty"
                  maxLength={200}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p className="text-xs text-slate-400 mt-1">Short label shown in the AI prompt header.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Override Fact <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.overrideFact}
                  onChange={(e) => setForm((f) => ({ ...f, overrideFact: e.target.value }))}
                  placeholder="Enter the exact text the AI should use when answering questions about this topic..."
                  maxLength={2000}
                  rows={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {form.overrideFact.length}/2000 chars. This text is injected verbatim into the AI system prompt.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Trigger Keywords <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.searchTerms}
                  onChange={(e) => setForm((f) => ({ ...f, searchTerms: e.target.value }))}
                  placeholder="e.g. warranty, return, refund, damaged"
                  maxLength={500}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Comma-separated. Leave blank to always inject (global fact).
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <input
                    type="number"
                    min={0}
                    max={999}
                    value={form.priority}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))
                    }
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">Lower = higher priority.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Starts On <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={form.startsAt}
                    onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">Auto-activates on this date.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Expires On <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">Auto-deactivates after this date.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                  aria-label={form.isActive ? "Deactivate" : "Activate"}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    form.isActive ? "bg-blue-600" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      form.isActive ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-sm text-slate-700">
                  {form.isActive ? "Active — will be injected into AI prompts" : "Inactive — will be skipped"}
                </span>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex gap-3 justify-end">
              <button
                onClick={closeForm}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? "Saving…" : editTarget ? "Save Changes" : "Create Override"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
